// ═══════════════════════════════════════════════════════════════════════════
// COLLAB-LOCK-COMMAND — loopback WS client core for the `/collab/ws` channel
// ═══════════════════════════════════════════════════════════════════════════
//
// The in-page loopback WS client that pairs with the ALREADY-MERGED
// node-companion collab broker (`apps/node-companion/src/collabServer.ts`,
// `CollabBroker`, mounted at `/collab/ws`). Mirrors the lifecycle shape of
// `apps/web/src/app/agentBridge/agentBridgeClient.ts` (connect / hello /
// welcome / heartbeat / reconnect) but is scoped to ONLY the soft-lock
// surface this PR needs — no presence tracking, no edit-operation relay, no
// owner-arbitration flow (those remain the deferred S-COLLAB-* slices).
//
// WHAT THIS CLIENT OWNS:
//   • WS lifecycle connect(url, {token,documentId,identity}) / disconnect() /
//     bye() — the token rides the HELLO FRAME (sent on socket `open`), never
//     the URL/query, mirroring the agent-bridge posture.
//   • `welcome` parse → cache `yourParticipantId` + seed the live `locks`
//     map from the bounded join snapshot.
//   • `requestLocks` / `releaseLocks` — correlate the companion's DIRECT
//     replies (`lock-granted`/`lock-denied`) and the room-wide `lock-update`
//     broadcast back to the in-flight caller, by SCOPE (the lock protocol
//     carries no `opId`).
//   • Live `locks: Map<scopeKey, LockState>` kept in sync for as long as the
//     socket is open — this is the map `collabLockVeto.ts` (via
//     `collabLockStore.ts`) reads to fail-closed-reject a conflicting edit.
//   • Heartbeat: answer the companion's app-level `ping` with `pong`
//     (REUSES `HeartbeatResponder` — the wire shape is byte-identical to the
//     agent-bridge heartbeat, `{type:"ping"|"pong", seq:number}`).
//   • Reconnect-on-unexpected-close (REUSES `ReconnectScheduler` +
//     `shouldAutoReconnect` — the collab broker uses the SAME
//     `WS_CLOSE_POLICY` (1008) + the SAME `hello-deadline`/`lease-expired`
//     retryable-reason vocabulary as the agent-bridge broker, so the
//     existing decision matrix applies verbatim).
//   • DEFAULT-OFF: constructing this class attaches nothing and opens no
//     socket — only `connect()` does.
//
// ── The `except: socket` asymmetry (READ BEFORE TOUCHING THE FRAME DISPATCH) ──
// `collabServer.ts`'s `handleLockRequest` broadcasts a GRANT to the room
// EXCLUDING the granter's own socket (`this.broadcast(roomId, frame,
// socket)`) — the granter already learned the outcome via the DIRECT
// `lock-granted` reply. So `onLockGranted` below must self-apply the grant
// into the live `locks` map (it will NEVER arrive via `lock-update` for the
// participant that requested it). `handleLockRelease`, by contrast,
// broadcasts to EVERY socket INCLUDING the releaser ("so its UI clears the
// lock badge") — so `releaseLocks()` correctly resolves off the NEXT
// `lock-update` alone, with no self-apply needed.
//
// DEP-GRAPH (ПРИОРИТЕТ #2): imports ONLY the pure `collabLockFrameCodec.ts` /
// `collabLockTypes.ts` siblings + the REUSED agent-bridge leaf modules
// (`heartbeat.ts`, `reconnect.ts`, `loopbackGuard.ts`, `clientTypes.ts`'s DI
// socket types) — NEVER editorStore, NEVER node-companion.
// ═══════════════════════════════════════════════════════════════════════════

import {
  type BridgeSocketFactory,
  type BridgeSocketLike,
} from "../agentBridge/clientTypes";
import { HeartbeatResponder } from "../agentBridge/heartbeat";
import { assertLoopbackWsTarget } from "../agentBridge/loopbackGuard";
import {
  ReconnectScheduler,
  shouldAutoReconnect,
  WS_CLOSE_NORMAL,
  type ReconnectOptions,
} from "../agentBridge/reconnect";
import {
  buildByeFrame,
  buildHelloFrame,
  buildLockReleaseFrame,
  buildLockRequestFrame,
  parseCollabInboundFrame,
  serializeCollabOutboundFrame,
} from "./collabLockFrameCodec";
import {
  lockScopeKey,
  type LockProposalOutcome,
  type LockScope,
  type LockState,
  type ParticipantIdentity,
} from "./collabLockTypes";

/** The hardcoded loopback collab connect target — the companion's SharedWsStack `/collab/ws` path, same port as the agent-bridge channel (8765). */
export const COLLAB_LOCK_WS_URL = "ws://127.0.0.1:8765/collab/ws";

/** Bounded default wait for a `lock-release` to be confirmed via `lock-update` before resolving "no observed effect". */
export const DEFAULT_RELEASE_CONFIRM_TIMEOUT_MS = 5_000;

/**
 * P2#3 (post-review) — bounded default wait for a `lock-request`'s DIRECT
 * `lock-granted`/`lock-denied` reply before that scope's promise REJECTS
 * (never hangs forever). Mirrors `DEFAULT_RELEASE_CONFIRM_TIMEOUT_MS` — the
 * companion is a loopback peer, so a normal reply arrives in well under a
 * second; a buggy/wedged/malicious companion that never replies must not
 * wedge the caller's `.then()` forever (the CLI/audit-feed callback that
 * reports the outcome would otherwise never fire).
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 5_000;

/**
 * P2#3 (post-review) — client-side ceiling on in-flight `requestLocks`
 * entries, mirroring the companion's own `COLLAB_LIMITS.locksPerParticipant`
 * (`apps/node-companion/src/collabTypes.ts`, 512). Without a cap a peer that
 * never replies (and never times out fast enough relative to request rate)
 * could grow `pendingRequests` unboundedly; a new `requestLocks()` call is
 * refused outright (before sending anything) once it would push the map past
 * this bound.
 *
 * P3 (post-review, round 2) — the cap counts only the NET-NEW scopes a call
 * would add (scopes NOT already a key in `pendingRequests`), never
 * `scopes.length` verbatim. A scope that is already pending gets its entry
 * SUPERSEDED (replaced in place, see the per-scope logic below), not
 * appended — it does not grow `pendingRequests.size` — so counting it as a
 * new addition would incorrectly refuse an at-capacity retry that re-requests
 * already-pending scopes even though the post-call size would be unchanged.
 */
export const MAX_PENDING_LOCK_REQUESTS = 512;

/**
 * P1 (post-review, round 3) — the WS close code the client uses when a
 * `welcome` frame arrives with `snapshot.truncated === true` (see the
 * `"welcome"` case in `onMessage` below). A distinct browser-ALLOWED app
 * code (`isBrowserAllowedCloseCode` in `../agentBridge/reconnect.ts`
 * documents why the reserved `1002`-family codes THROW synchronously on
 * `WebSocket.close()`) — mirrors `WS_CLOSE_PROTOCOL_MISMATCH`'s convention
 * (`agentBridgeClient.ts`'s protocol-version-mismatch teardown) for "this
 * connection cannot proceed safely, tear it down with a distinct reason".
 * The companion does not branch on the exact value (informational only).
 */
const WS_CLOSE_TRUNCATED_SNAPSHOT = 4010;

/**
 * The 4 coarse connection states this client surfaces (no active/observer
 * distinction — every collab participant is a symmetric room member).
 * `"error"` also covers the P1 (post-review, round 3) truncated-welcome
 * fail-closed refusal — see `onMessage`'s `"welcome"` case.
 */
export type CollabLockConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "error";

/** The params `connect()` needs beyond the target URL. */
export type CollabLockConnectParams = {
  token: string;
  documentId: string;
  identity: ParticipantIdentity;
};

/** Constructor DI — every editor/store coupling arrives through callbacks, never a direct store import (mirrors `AgentBridgeClientDeps`). */
export type CollabLockClientDeps = {
  /** Surface a coarse status change (the store maps this straight through). */
  onStatusChange?: (status: CollabLockConnectionStatus) => void;
  /**
   * Fired whenever the LIVE tracked state changes (welcome seed, a
   * `lock-update` delta, or a reset on disconnect/drop) — the store mirrors
   * this into its own reactive `locks` + `selfParticipantId` fields.
   */
  onLocksSync?: (
    locks: ReadonlyMap<string, LockState>,
    selfParticipantId: string | null,
  ) => void;
  /** Open a socket (DI; default `new WebSocket`). */
  socketFactory?: BridgeSocketFactory;
  /** Reconnect backoff tuning (DI for deterministic tests). */
  reconnect?: ReconnectOptions;
  /** Bounded wait (ms) for a release to confirm via `lock-update` (DI; default `DEFAULT_RELEASE_CONFIRM_TIMEOUT_MS`). */
  releaseTimeoutMs?: number;
  /** Bounded wait (ms) for a request's grant/deny reply before it REJECTS (DI; default `DEFAULT_REQUEST_TIMEOUT_MS`). */
  requestTimeoutMs?: number;
  /** Client-side ceiling on in-flight `requestLocks` entries (DI for deterministic cap tests; default `MAX_PENDING_LOCK_REQUESTS`). */
  maxPendingRequests?: number;
  /** Timer arm (default `setTimeout`). Injected so a test drives the release-timeout deterministically. */
  setTimer?: (cb: () => void, ms: number) => ReturnType<typeof setTimeout>;
  /** Timer clear (default `clearTimeout`). Injected to pair with `setTimer`. */
  clearTimer?: (handle: ReturnType<typeof setTimeout>) => void;
};

type PendingRequest = {
  resolve: (outcome: LockProposalOutcome) => void;
  reject: (err: Error) => void;
  /** P2#3 — the bounded request-timeout timer; cleared on ANY settlement (grant/deny/supersede/disconnect) so it can never fire late. */
  timer: ReturnType<typeof setTimeout>;
};

type PendingRelease = {
  resolve: (confirmed: boolean) => void;
  timer: ReturnType<typeof setTimeout>;
  /**
   * P3 (post-review, round 5) — whether this client believed it owned the
   * scope at the moment `releaseLocks()` sent the frame. Gates whether a
   * later observed `lock-update` release for this scope may be attributed
   * to THIS request (`true`) or merely coincides with it (`false` — see
   * `onMessage`'s `"lock-update"` case).
   */
  ownedBySelfAtRequest: boolean;
};

/**
 * The loopback collab-lock WS client. DEFAULT-OFF: constructing it attaches
 * nothing and opens no socket — `connect()` is the only thing that opens
 * one.
 */
export class CollabLockClient {
  private readonly deps: CollabLockClientDeps;

  private readonly socketFactory: BridgeSocketFactory;

  /**
   * P3 (post-review, round 3) — the factory ACTUALLY in effect for the
   * current session: `connect()`'s per-call `socketFactory` argument when
   * supplied, else the constructor-level `this.socketFactory`. Every
   * `openSocket()` call — the INITIAL connect AND every subsequent
   * reconnect the same session schedules — reads THIS field, not
   * `this.socketFactory` directly, so a per-call override (a test injecting
   * a sequential mock transport, say) stays in effect for the WHOLE
   * session, not just the first socket. Re-derived on every `connect()`
   * call (see there), so it can never leak a stale override into the NEXT
   * session.
   */
  private activeSocketFactory: BridgeSocketFactory;

  private readonly reconnectScheduler: ReconnectScheduler;

  private readonly releaseTimeoutMs: number;

  private readonly requestTimeoutMs: number;

  private readonly maxPendingRequests: number;

  private readonly setTimer: NonNullable<CollabLockClientDeps["setTimer"]>;

  private readonly clearTimer: NonNullable<CollabLockClientDeps["clearTimer"]>;

  private socket: BridgeSocketLike | null = null;

  private heartbeat: HeartbeatResponder | null = null;

  private status: CollabLockConnectionStatus = "disconnected";

  /** Whether the CURRENT socket reached `welcome` (per-connection; reset on every `openSocket`). */
  private welcomed = false;

  /** Whether the current close is a deliberate `disconnect()` (suppresses reconnect). */
  private deliberateClose = false;

  /** The connect target for this session (set by `connect()`, re-used across reconnects). */
  private wsUrl: string | null = null;

  private connectParams: CollabLockConnectParams | null = null;

  private yourParticipantId: string | null = null;

  /** The LIVE tracked lock map, keyed by `lockScopeKey`. Authoritative while the socket is open. */
  private readonly locks = new Map<string, LockState>();

  private readonly pendingRequests = new Map<string, PendingRequest>();

  private readonly pendingReleases = new Map<string, PendingRelease>();

  /**
   * GitHub Codex review (PR #534) P2 — bumped by `rejectAllPending()`
   * (called from exactly `onClose()` and `disconnect()`, i.e. every
   * connection-teardown path). `requestLocks()` snapshots this at the START
   * of a batch and re-checks it once every entry has settled: if the
   * connection tore down AT ALL during the batch's lifetime, the returned
   * map is forced empty regardless of which individual scopes fulfilled.
   * Without this, a scope granted EARLY in a multi-scope batch (self-applied
   * into `this.locks`, `syncLocks()` fired) would still be reported
   * "granted" to the caller even after a LATER teardown in the SAME batch
   * has already cleared `this.locks` via `resetTrackedState()` — the
   * companion releases every lock held by a disconnecting participant, so
   * that grant is stale/moot the instant the connection drops, and
   * reporting it as live would be actively misleading.
   */
  private connectionEpoch = 0;

  constructor(deps: CollabLockClientDeps = {}) {
    this.deps = deps;
    this.socketFactory = deps.socketFactory ?? ((url) => new WebSocket(url) as unknown as BridgeSocketLike);
    this.activeSocketFactory = this.socketFactory;
    this.reconnectScheduler = new ReconnectScheduler(deps.reconnect);
    this.releaseTimeoutMs = deps.releaseTimeoutMs ?? DEFAULT_RELEASE_CONFIRM_TIMEOUT_MS;
    this.requestTimeoutMs = deps.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
    this.maxPendingRequests = deps.maxPendingRequests ?? MAX_PENDING_LOCK_REQUESTS;
    this.setTimer = deps.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
    this.clearTimer = deps.clearTimer ?? ((handle) => clearTimeout(handle));
  }

  // ── public surface ─────────────────────────────────────────────────────

  getStatus(): CollabLockConnectionStatus {
    return this.status;
  }

  /** The server-minted participant id from the last `welcome`, or `null` before pairing / after a reset. */
  getOwnParticipantId(): string | null {
    return this.yourParticipantId;
  }

  /** A synchronous snapshot of every currently-tracked live lock. */
  getActiveLocks(): readonly LockState[] {
    return [...this.locks.values()];
  }

  /**
   * Open the loopback WS and pair. The token rides the HELLO FRAME (sent on
   * `onopen`), never the URL/query. `socketFactory`, when supplied,
   * OVERRIDES the constructor-level DI for the ENTIRE session this
   * `connect()` call starts — the initial socket AND every reconnect
   * `onClose` schedules while this session stays up (P3, post-review round
   * 3: `activeSocketFactory` is what `openSocket()` actually reads, both
   * here and from the reconnect scheduler — a per-call override used to be
   * silently DROPPED after the first socket, reverting every reconnect to
   * the constructor default). Re-derived fresh on every `connect()` call, so
   * it never leaks into a later, unrelated session.
   */
  connect(url: string, params: CollabLockConnectParams, socketFactory?: BridgeSocketFactory): void {
    if (this.socket !== null) {
      return; // already connected / connecting (single socket).
    }
    this.wsUrl = url;
    this.connectParams = params;
    this.deliberateClose = false;
    this.reconnectScheduler.cancel();
    this.reconnectScheduler.reset();
    assertLoopbackWsTarget(url); // refuse a non-loopback target up front (fail-closed).
    this.activeSocketFactory = socketFactory ?? this.socketFactory;
    this.openSocket(this.activeSocketFactory);
  }

  /**
   * Best-effort explicit graceful leave (server closes 1000 on receipt).
   * No-op if not open+welcomed.
   *
   * P2 (post-review, round 5) — sets `deliberateClose` BEFORE sending the
   * frame (mirrors `disconnect()`, which calls `bye()` then sets it itself —
   * setting it here too is harmless/idempotent for that call path). Without
   * this, a STANDALONE `bye()` call (not routed through `disconnect()`) left
   * `deliberateClose` at its prior value; the server's resulting 1000 close
   * then reached `onClose()`, which reconnects into the room the caller just
   * deliberately left (`shouldAutoReconnect` only suppresses reconnect when
   * `deliberateClose` is `true`).
   */
  bye(): void {
    if (this.socket === null || !this.welcomed) return;
    this.deliberateClose = true;
    this.rawSend(serializeCollabOutboundFrame(buildByeFrame()));
  }

  /**
   * Deliberately tear down the socket (suppresses reconnect). Every
   * in-flight `requestLocks()` promise REJECTS (the outcome is now
   * undeliverable); every in-flight `releaseLocks()` scope resolves `false`
   * ("no observed effect") — mirrors the release-timeout semantics rather
   * than inventing a second failure shape for the same "no confirmation
   * arrived" case.
   */
  disconnect(): void {
    this.bye();
    this.deliberateClose = true;
    this.reconnectScheduler.cancel();
    this.reconnectScheduler.reset();
    const s = this.socket;
    if (s !== null) {
      this.socket = null;
      this.detachHandlers(s);
      try {
        s.close(WS_CLOSE_NORMAL, "client-disconnect");
      } catch {
        /* close on an already-closed socket is harmless */
      }
    }
    this.heartbeat = null;
    this.welcomed = false;
    this.connectParams = null;
    this.wsUrl = null;
    this.rejectAllPending(new Error("collab-lock-client: disconnected"));
    this.resetTrackedState();
    this.setStatus("disconnected");
  }

  /**
   * Send one `lock-request` per scope and resolve each against its matching
   * DIRECT `lock-granted`/`lock-denied` reply (correlated by
   * `lockScopeKey` — the protocol carries no `opId` on lock frames).
   * Rejects the WHOLE batch if the connection drops before every scope
   * settled (fail-closed — never hangs forever).
   *
   * P2#2/P2#3 (post-review) — per-scope hardening:
   *   - A SECOND in-flight `requestLocks` for the SAME scope (while the
   *     first has not yet settled) REJECTS the first's promise before
   *     overwriting the pending entry — the stale call's `Promise.all` chain
   *     would otherwise wedge forever (a different chain than this new
   *     call's, so rejecting it does not affect the new call).
   *   - Each scope has its OWN bounded timeout (`requestTimeoutMs`): if
   *     neither a `lock-granted`/`lock-denied` reply nor a supersede/
   *     disconnect settles it in time, the promise REJECTS (a timeout is a
   *     client-side infrastructure failure, not a protocol outcome — it is
   *     therefore a rejection, never a synthetic `LockProposalOutcome`).
   *     The timer is cleared on every OTHER settlement path so it can never
   *     fire late against an already-settled promise.
   *   - `pendingRequests.size` is capped (`MAX_PENDING_LOCK_REQUESTS`,
   *     mirroring the companion's own per-participant lock cap) — a call
   *     that would exceed it is refused outright, before sending anything.
   *     Only the NET-NEW scopes (those not already a key in
   *     `pendingRequests`) count against the cap — a scope that is already
   *     pending is SUPERSEDED in place (see above), not appended, so
   *     re-requesting already-pending scopes at capacity does not grow the
   *     map and must not be refused.
   *   - P2 (post-review, round 5) — `scopes` is deduped by `lockScopeKey`
   *     BEFORE the per-scope promise array is built (see below), so a scope
   *     repeated WITHIN one call never triggers its own same-call "supersede"
   *     path (which would otherwise reject the batch's own first entry and
   *     leave it missing from the outcome map, even though the surviving
   *     grant/deny would have settled it correctly).
   *   - GitHub Codex review (PR #534, f0db1ad6) P2 — settles via
   *     `Promise.allSettled`, NEVER `Promise.all`. `Promise.all` fails FAST:
   *     the instant ONE scope's entry rejects (a timeout, or a same-scope
   *     supersede from a LATER, separate `requestLocks()` call), it rejects
   *     the WHOLE returned promise WITHOUT waiting for the other scopes in
   *     THIS batch, which are still genuinely pending. The caller
   *     (`dispatchCollabLockCommand`) then reports the ENTIRE `LOCK` as
   *     failed — but those other scopes keep waiting in the background, and
   *     if the companion grants them moments later, `this.locks` silently
   *     starts tracking a lock NOBODY was told about (the audit line already
   *     said "failed"): the client now unknowingly holds a lock, blocking
   *     every other collaborator from acquiring it, with no way for the
   *     local user to discover it needs releasing. `allSettled` waits for
   *     EVERY scope to genuinely settle before resolving at all, so a late
   *     grant always lands inside the returned map — a rejected entry is
   *     simply ABSENT from it, which the caller already treats identically
   *     to a deny (`outcome === undefined` branch, `dispatchCollabLockCommand`
   *     in `editorStore.ts`) — no caller-side change needed.
   */
  requestLocks(scopes: readonly LockScope[]): Promise<Map<string, LockProposalOutcome>> {
    if (this.socket === null || !this.welcomed) {
      return Promise.reject(
        new Error("collab-lock-client: requestLocks called while not connected"),
      );
    }
    // P2 (post-review, round 5) — dedupe by scope key BEFORE building the
    // per-scope promise array. Without this, `requestLocks([sameScope,
    // sameScope])` — a caller passing the SAME scope twice within ONE call
    // (e.g. the bare LOCK command building scopes from
    // `state.selection.entityIds`, which is not itself deduped) — builds TWO
    // promises for the SAME key; the SECOND array entry's "supersede" logic
    // (below) synchronously REJECTS the FIRST entry's promise (correct for a
    // genuinely SEPARATE later call, wrong here — it is the SAME call), and
    // `Promise.all` rejects the WHOLE batch the instant that happens, even
    // though the surviving promise would have resolved with a real grant.
    // Deduping collapses same-key duplicates to ONE request/promise; every
    // duplicate scope in the caller's ORIGINAL array still resolves
    // correctly via the returned Map's shared `lockScopeKey` lookup
    // (`dispatchCollabLockCommand` reads `outcomes.get(lockScopeKey(scope))`
    // per scope — a repeated scope just looks up the SAME entry twice,
    // harmlessly).
    const uniqueScopes = [...new Map(scopes.map((scope) => [lockScopeKey(scope), scope])).values()];
    // Count only the NET-NEW scopes against the cap: a scope that already
    // has a pending entry gets SUPERSEDED in place (below), not appended, so
    // it must not count as a new addition — otherwise an at-capacity retry
    // of already-pending scopes (a net-zero change to `pendingRequests.size`)
    // would be incorrectly refused. `existingKeys` is snapshotted BEFORE any
    // mutation; `uniqueScopes` (not `scopes`) is the source here so a scope
    // repeated within THIS SAME call is counted once, not twice.
    const existingKeys = new Set(this.pendingRequests.keys());
    const netNewKeys = new Set(
      uniqueScopes.map((scope) => lockScopeKey(scope)).filter((key) => !existingKeys.has(key)),
    );
    if (this.pendingRequests.size + netNewKeys.size > this.maxPendingRequests) {
      return Promise.reject(
        new Error(
          "collab-lock-client: requestLocks refused — too many in-flight lock requests",
        ),
      );
    }
    const entries = uniqueScopes.map(
      (scope) =>
        new Promise<[string, LockProposalOutcome]>((resolve, reject) => {
          const key = lockScopeKey(scope);
          // A second in-flight request for the SAME scope supersedes the
          // first pending entry — reject THAT promise (its own separate
          // `Promise.all` chain) before overwriting it, so the stale caller
          // is told what happened instead of hanging forever.
          const existing = this.pendingRequests.get(key);
          if (existing !== undefined) {
            this.clearTimer(existing.timer);
            existing.reject(
              new Error(
                "collab-lock-client: requestLocks for this scope was superseded by a newer request before the companion replied",
              ),
            );
          }
          const timer = this.setTimer(() => {
            this.pendingRequests.delete(key);
            reject(
              new Error(
                "collab-lock-client: requestLocks timed out waiting for a reply",
              ),
            );
          }, this.requestTimeoutMs);
          this.pendingRequests.set(key, {
            resolve: (outcome) => resolve([key, outcome]),
            reject,
            timer,
          });
          this.rawSend(serializeCollabOutboundFrame(buildLockRequestFrame(scope)));
        }),
    );
    // GitHub Codex review (PR #534, round 2) P2 — snapshotted BEFORE
    // `entries` can settle, so a connection teardown that happens partway
    // through THIS batch (`rejectAllPending` bumps it) is detected even if
    // every entry ends up settling (a scope granted early, self-applied
    // into `this.locks`, then the socket drops before the rest reply).
    const epochAtStart = this.connectionEpoch;
    return Promise.allSettled(entries).then((results) => {
      if (this.connectionEpoch !== epochAtStart) {
        // The connection tore down at some point during this batch. Every
        // lock this client believed it held was already cleared by
        // `resetTrackedState()` (the companion releases everything a
        // disconnecting participant held) — an earlier-fulfilled entry in
        // `results` would report a "granted" outcome that is now stale and
        // actively misleading. Report nothing rather than something false.
        return new Map<string, LockProposalOutcome>();
      }
      const pairs: Array<[string, LockProposalOutcome]> = [];
      for (const result of results) {
        if (result.status === "fulfilled") pairs.push(result.value);
      }
      return new Map(pairs);
    });
  }

  /**
   * Send one `lock-release` per scope. The companion does NOT ack a release
   * individually (`collabServer.ts` `handleLockRelease` only broadcasts a
   * `lock-update` on success — a release of a scope the caller does not own,
   * or does not hold, silently no-ops). Each scope resolves `true` once it
   * disappears from the live `locks` map via the next `lock-update`
   * AND the client can attribute that release to THIS request (P3,
   * post-review round 5 — see `ownedBySelfAtRequest` below), or `false`
   * after `releaseTimeoutMs` with no such attributable delta observed
   * (treated as "the release had no effect" — NEVER hangs forever).
   *
   * P2 (post-review, round 5) — `scopes` is deduped by `lockScopeKey`
   * BEFORE the per-scope promise array is built, mirroring `requestLocks()`
   * (same class of bug: a scope repeated within one call would otherwise
   * fire a redundant `lock-release` frame and superseded-resolve its own
   * first promise unnecessarily).
   */
  releaseLocks(scopes: readonly LockScope[]): Promise<Map<string, boolean>> {
    if (this.socket === null || !this.welcomed) {
      return Promise.reject(
        new Error("collab-lock-client: releaseLocks called while not connected"),
      );
    }
    const uniqueScopes = [...new Map(scopes.map((scope) => [lockScopeKey(scope), scope])).values()];
    const entries = uniqueScopes.map(
      (scope) =>
        new Promise<[string, boolean]>((resolve) => {
          const key = lockScopeKey(scope);
          // P3 (post-review, round 5) — "Avoid confirming releases caused
          // by another owner". Snapshot whether THIS client believes it
          // owns the scope RIGHT NOW, before sending the release frame. The
          // `lock-update` handler (see `onMessage`'s `"lock-update"` case)
          // resolves this promise `true` ONLY when this snapshot was `true`
          // — a release observed for a scope this client did NOT hold at
          // request time cannot be attributed to THIS request (it may be a
          // totally unrelated participant's own release, or a lease
          // expiry, that happens to land in the same window), so it
          // resolves `false` ("no confirmed effect from MY request") even
          // though the scope genuinely did become unlocked. Inferred
          // entirely from state already tracked client-side — no wire-
          // protocol change.
          const ownedBySelfAtRequest = this.locks.get(key)?.ownerId === this.yourParticipantId;
          // A second in-flight release for the SAME scope replaces the first
          // pending entry — the stale timer is cleared so it cannot fire
          // late and doubly-resolve a settled promise, and the stale
          // promise is resolved `false` ("superseded — no confirmed
          // effect", consistent with this function's existing true/false
          // contract; releaseLocks never rejects per-scope) so it does not
          // hang forever either.
          const existing = this.pendingReleases.get(key);
          if (existing !== undefined) {
            this.clearTimer(existing.timer);
            existing.resolve(false);
          }
          const timer = this.setTimer(() => {
            this.pendingReleases.delete(key);
            resolve([key, false]);
          }, this.releaseTimeoutMs);
          this.pendingReleases.set(key, {
            resolve: (confirmed) => resolve([key, confirmed]),
            timer,
            ownedBySelfAtRequest,
          });
          this.rawSend(serializeCollabOutboundFrame(buildLockReleaseFrame(scope)));
        }),
    );
    return Promise.all(entries).then((pairs) => new Map(pairs));
  }

  // ── socket lifecycle ─────────────────────────────────────────────────────

  private openSocket(factory: BridgeSocketFactory): void {
    this.welcomed = false;
    this.setStatus("connecting");
    let socket: BridgeSocketLike;
    try {
      socket = factory(this.wsUrl ?? COLLAB_LOCK_WS_URL);
    } catch {
      this.setStatus("error");
      return;
    }
    this.socket = socket;
    this.heartbeat = new HeartbeatResponder((text) => this.rawSend(text));
    socket.onopen = () => this.onOpen();
    socket.onclose = (event) => this.onClose(event.code, event.reason);
    socket.onerror = () => this.onError();
    socket.onmessage = (event) => this.onMessage(event.data);
  }

  private onOpen(): void {
    const params = this.connectParams;
    if (params === null) return; // defensive: cannot happen (connect() always sets it first).
    this.rawSend(
      serializeCollabOutboundFrame(
        buildHelloFrame({
          token: params.token,
          documentId: params.documentId,
          identity: params.identity,
        }),
      ),
    );
  }

  private onError(): void {
    if (!this.welcomed) {
      this.setStatus("error");
    }
  }

  private onClose(code: number, reason?: string): void {
    const socket = this.socket;
    if (socket !== null) this.detachHandlers(socket);
    this.socket = null;
    this.heartbeat = null;
    const wasWelcomed = this.welcomed;
    this.rejectAllPending(new Error("collab-lock-client: connection closed"));
    this.resetTrackedState();

    const willReconnect = shouldAutoReconnect(code, this.deliberateClose, reason, wasWelcomed);
    if (!willReconnect) {
      if (this.deliberateClose) {
        // Local Codex review (post-round-6, pre-PR) P2 — `disconnect()` and
        // the truncated-welcome rejection (`onMessage`'s "welcome" case)
        // both detach the socket's handlers BEFORE calling `close()`, so
        // NEITHER of those `deliberateClose = true` sites ever reaches
        // `onClose()` at all (their own `setStatus` call already ran
        // synchronously). A STANDALONE `bye()` call is the only remaining
        // way to land here with `deliberateClose` true: it sends the frame
        // but does not close the socket client-side (the server does, per
        // its own doc comment), so this handler — still fully attached —
        // fires for real. Before this fix that fell through to a silent
        // `return`, leaving `status` stuck at whatever it was (typically
        // "connected") even though `socket` is now `null` and
        // `resetTrackedState()` already cleared every tracked lock —
        // observers (status-dependent UI, lock listing) kept believing the
        // session was still live. Report the graceful leave explicitly.
        this.setStatus("disconnected");
        return;
      }
      this.setStatus("error");
      return;
    }
    this.setStatus("connecting");
    this.reconnectScheduler.schedule(() => {
      if (this.socket === null && this.connectParams !== null && !this.deliberateClose) {
        // P3 (post-review, round 3) — reuse the SESSION's active factory
        // (the `connect()` per-call override when one was supplied), not
        // the constructor-level default directly — see `activeSocketFactory`'s
        // doc comment.
        this.openSocket(this.activeSocketFactory);
      }
    });
  }

  private detachHandlers(socket: BridgeSocketLike): void {
    socket.onopen = null;
    socket.onclose = null;
    socket.onerror = null;
    socket.onmessage = null;
  }

  // ── inbound frame dispatch ───────────────────────────────────────────────

  private onMessage(data: unknown): void {
    let parsed: unknown;
    try {
      parsed = typeof data === "string" ? JSON.parse(data) : data;
    } catch {
      return; // malformed JSON → drop the single frame (fail-closed, no disconnect).
    }
    const frame = parseCollabInboundFrame(parsed);
    if (frame === null) return;
    // P3 (post-review, round 4) — defensive: drop EVERY non-`welcome` frame
    // until `welcomed` is true. The current broker sends `welcome` first by
    // construction (FIFO per-socket), so this is not reachable against
    // TODAY's server — but the invariant "never trust `locks` before the
    // truncated-snapshot check has run" (see the P1 fix below) should not
    // depend on a cross-file ordering guarantee holding forever. Mirrors the
    // fail-closed posture of the rest of this file: an out-of-order frame is
    // silently dropped (single frame, no disconnect), never acted on.
    if (frame.type !== "welcome" && !this.welcomed) return;
    switch (frame.type) {
      case "welcome": {
        if (frame.snapshot.truncated) {
          // P1 (post-review, round 3) — `collabServer.ts`'s
          // `boundedWelcomeSnapshot` trims the lock list once the room's
          // aggregate peers+locks payload exceeds the per-frame ceiling,
          // setting `truncated: true`. Promoting to `connected` here would
          // seed `locks` with a KNOWN-INCOMPLETE picture —
          // `evaluateCollabLockVeto` (via `collabLockStore.ts`) trusts this
          // map as authoritative, so it would silently ALLOW an edit that IS
          // actually locked server-side but got trimmed from the snapshot,
          // defeating the feature's fail-closed guarantee for whichever
          // locks were dropped. So this refuses to operate on a
          // known-incomplete lock picture — fail-closed at the CONNECTION
          // level (never `welcomed`, never `connected`) rather than silently
          // under-protecting; a full paginated lock-resync protocol is
          // future work. Mirrors `agentBridgeClient.ts`'s
          // protocol-version-mismatch teardown: close the unusable socket
          // deliberately (no auto-reconnect looping the same truncation) and
          // surface `"error"` — the caller observes the failure via
          // `getStatus()`/`onStatusChange` (`connect()` is fire-and-forget,
          // it returns no promise).
          this.deliberateClose = true;
          const s = this.socket;
          if (s !== null) {
            this.socket = null;
            this.detachHandlers(s);
            try {
              s.close(WS_CLOSE_TRUNCATED_SNAPSHOT, "welcome-snapshot-truncated");
            } catch {
              /* harmless — defensive only; the code is browser-allowed so this should not throw */
            }
          }
          this.heartbeat = null;
          this.setStatus("error");
          // `welcomed` was already `false` (set at the top of `openSocket`
          // and never flipped in this branch) — `resetTrackedState()` also
          // clears `yourParticipantId`/`locks` (both still empty here) and
          // re-syncs the store, mirroring every other teardown path.
          this.resetTrackedState();
          return;
        }
        this.yourParticipantId = frame.participantId;
        this.welcomed = true;
        this.locks.clear();
        for (const lock of frame.snapshot.locks) {
          this.locks.set(lockScopeKey(lock.scope), lock);
        }
        this.reconnectScheduler.cancel();
        this.reconnectScheduler.reset();
        this.setStatus("connected");
        this.syncLocks();
        return;
      }
      case "lock-granted": {
        // Self-apply — the companion's broadcast for MY OWN grant excludes my
        // socket (see the module header's `except: socket` note), so this is
        // the ONLY place the granter learns of its own successful lock.
        const key = lockScopeKey(frame.lock.scope);
        const pending = this.pendingRequests.get(key);
        // GitHub Codex review (PR #534, round 2) P2 — only self-apply when a
        // LIVE pending request is still tracking this exact scope. Before
        // this fix, a late reply arriving AFTER this request's OWN timeout
        // already fired (`pendingRequests.delete(key)`, caller told
        // "absent/denied" via `requestLocks()`'s returned map) still landed
        // here and unconditionally recorded the grant into `this.locks` +
        // fired `syncLocks()` — the client silently starts HOLDING a
        // server-side lock the caller was never told about, indistinguishable
        // from every other tracked lock, blocking every other collaborator
        // until this client happens to discover and release it (or
        // disconnects). Proactively release an orphaned late grant instead of
        // retaining it — nobody is waiting for it anymore.
        if (pending !== undefined) {
          this.locks.set(key, frame.lock);
          this.syncLocks();
          this.pendingRequests.delete(key);
          this.clearTimer(pending.timer);
          pending.resolve({ status: "granted", lock: frame.lock });
        } else if (this.locks.has(key)) {
          // GitHub Codex review (PR #534, round 3) P2 — "Avoid releasing
          // superseded lock grants". Two OVERLAPPING `requestLocks()` calls
          // for the SAME scope (e.g. a user double-submitting `LOCK LAYER
          // Walls` before the first reply lands) each send their OWN
          // `lock-request` frame — `requestLocks()`'s per-scope supersede
          // logic only REJECTS/replaces the STALE `pendingRequests` entry,
          // it does not (and cannot) un-send the first frame. The companion
          // can legitimately reply `lock-granted` to BOTH (the second is a
          // same-owner renew, trivially satisfied) — the FIRST reply lands
          // above, resolves the (superseded, now-current) `pendingRequests`
          // entry, and deletes it; the SECOND reply then arrives here with
          // NO pending entry, even though it is genuinely reporting a lock
          // this client already holds (just self-applied a heartbeat ago by
          // the branch above), not one nobody is waiting for. Falling into
          // the orphan-release branch below would immediately release a
          // lock the caller was JUST told it holds. Distinguish "genuinely
          // orphaned" (no pending request AND not already held — the
          // original round-2 case, a late reply after OUR OWN timeout gave
          // up) from "duplicate/renewal for a scope we already hold" (this
          // case) by checking `this.locks` — refresh the stored record
          // (server is authoritative for `expiresAtMs` etc.) but do not
          // treat it as a stray grant to release.
          this.locks.set(key, frame.lock);
          this.syncLocks();
        } else {
          this.rawSend(serializeCollabOutboundFrame(buildLockReleaseFrame(frame.lock.scope)));
        }
        return;
      }
      case "lock-denied": {
        const key = lockScopeKey(frame.scope);
        const pending = this.pendingRequests.get(key);
        if (pending !== undefined) {
          this.pendingRequests.delete(key);
          this.clearTimer(pending.timer);
          pending.resolve({
            status: "denied",
            scope: frame.scope,
            reason: frame.reason,
            heldBy: frame.heldBy,
          });
        }
        return;
      }
      case "lock-update": {
        for (const lock of frame.granted) {
          this.locks.set(lockScopeKey(lock.scope), lock);
        }
        for (const scope of frame.released) {
          const key = lockScopeKey(scope);
          this.locks.delete(key);
          const pendingRelease = this.pendingReleases.get(key);
          if (pendingRelease !== undefined) {
            this.pendingReleases.delete(key);
            this.clearTimer(pendingRelease.timer);
            // P3 (post-review, round 5) — "Avoid confirming releases caused
            // by another owner". Resolve `true` ONLY when this client
            // believed it owned the scope at the moment it requested the
            // release (`ownedBySelfAtRequest`, snapshotted in
            // `releaseLocks()`) — otherwise this observed release cannot be
            // attributed to THIS client's own request (it may be a
            // DIFFERENT participant's own release, or a lease expiry, that
            // happens to land in the same window) and resolves `false`,
            // mirroring the existing timeout-based "no confirmed effect"
            // semantics documented on `releaseLocks()` rather than
            // fabricating a confirmation this client cannot prove it caused.
            pendingRelease.resolve(pendingRelease.ownedBySelfAtRequest);
          }
        }
        this.syncLocks();
        return;
      }
      case "error": {
        // Only `lock-refused` with a `scope` is meaningful to a pending
        // request (a room-membership-layer reject BEFORE the normal
        // grant/deny outcome — see `collabRoom.ts` `requesterFor`). Every
        // other error code belongs to an out-of-scope subsystem (op /
        // arbitration) and is ignored here.
        if (frame.code === "lock-refused" && frame.scope !== undefined) {
          const key = lockScopeKey(frame.scope);
          const pending = this.pendingRequests.get(key);
          if (pending !== undefined) {
            this.pendingRequests.delete(key);
            this.clearTimer(pending.timer);
            // The room-membership reject reasons ("unknown-participant" /
            // "role-cannot-edit") do not appear in the client's
            // `LockDenyReason` union (that union covers `locks.propose`'s
            // OWN role/limit gate) — both mean "you may not lock", so this
            // approximates with the closest matching typed reason rather
            // than inventing a new taxonomy member for a rare room-layer
            // edge case.
            pending.resolve({
              status: "denied",
              scope: frame.scope,
              reason: "role-cannot-lock",
              heldBy: null,
            });
          }
        }
        return;
      }
      case "ping":
        this.heartbeat?.onPing(frame);
        return;
      default:
        return;
    }
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private syncLocks(): void {
    this.deps.onLocksSync?.(new Map(this.locks), this.yourParticipantId);
  }

  private resetTrackedState(): void {
    this.welcomed = false;
    this.yourParticipantId = null;
    this.locks.clear();
    this.syncLocks();
  }

  private rejectAllPending(err: Error): void {
    this.connectionEpoch += 1;
    for (const pending of this.pendingRequests.values()) {
      this.clearTimer(pending.timer);
      pending.reject(err);
    }
    this.pendingRequests.clear();
    for (const pending of this.pendingReleases.values()) {
      this.clearTimer(pending.timer);
      pending.resolve(false);
    }
    this.pendingReleases.clear();
  }

  private rawSend(text: string): void {
    if (this.socket === null) return;
    try {
      this.socket.send(text);
    } catch {
      /* a send on a closing socket is harmless — the close handler runs next */
    }
  }

  private setStatus(status: CollabLockConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.deps.onStatusChange?.(status);
  }
}
